import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { api } from '@/api';
import { DEFAULT_DATE_FORMAT, RAW_DATE_FORMAT } from '@/common/constants';
import { Pagination } from '@/common/types';
import { API_URL } from '@/dtos';

import { TaxType } from '../../contributions.types';
import {
  PayrollCalendarStatus,
  ScheduledContributionFrequency,
  ScheduledContributionPayload,
  ScheduledContributionStatus,
} from '../ScheduledContribution.types';

export const SCHEDULED_CONTRIBUTION_QUERY_KEY = 'GET_SCHEDULED_CONTRIBUTION';
export interface ScheduledContributionResponse {
  calendarId: number;
  scheduledContributionId: number;
  calendarName: string;
  calendarStatus: PayrollCalendarStatus;
  frequencyType: ScheduledContributionFrequency;
  nextScheduledDate: string | null;
  startDate: string | null;
  planCodes: string[];
  taxType: TaxType;
  status: ScheduledContributionStatus;
}

interface Props {
  page?: number;
  perPage?: number;
}

const useScheduledContributionListQuery = ({ page, perPage }: Props) => {
  const { data, isLoading, isSuccess } = useQuery(
    [SCHEDULED_CONTRIBUTION_QUERY_KEY, page, perPage],
    () =>
      // TODO: DTO is not matching
      api.get<Pagination<ScheduledContributionResponse>>(API_URL.SCHEDULED_CONTRIBUTION_SEARCH_ORGANIZATION, {
        params: {
          ...(perPage ? { size: perPage } : {}),
          ...(page || page === 0 ? { page } : {}),
        },
      }),
    {
      retry: 0,
    }
  );

  const formattedData = useMemo<ScheduledContributionPayload[]>(() => {
    const list = data?.data.content || [];
    return list.map((item) => ({
      id: `${item.scheduledContributionId}`,
      calendarId: `${item.calendarId}`,
      name: item.calendarName,
      frequency: item.frequencyType,
      calendarStatus: item.calendarStatus,
      nextScheduledDate: item.nextScheduledDate,
      status: item.status,
      taxType: item.taxType,
      startDate: item.startDate ? dayjs(item.startDate, RAW_DATE_FORMAT).format(DEFAULT_DATE_FORMAT) : null,
      accounts: item.planCodes.map((account) => ({
        key: account,
        value: account,
        title: account,
      })),
    }));
  }, [data?.data]);
  return {
    formattedData,
    totalPages: data?.data?.totalPages,
    totalElements: data?.data?.totalElements,
    isLoading,
    isSuccess,
  };
};

export default useScheduledContributionListQuery;
